import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Users,
  Clock,
  DollarSign,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  AlertCircle,
  Info,
  Moon,
  Calendar,
  Building2,
  Loader2,
} from 'lucide-react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend
} from 'recharts';
import api from '../services/api';

const COLORS = ['#06b6d4', '#8b5cf6', '#f59e0b', '#10b981', '#ef4444', '#3b82f6'];

const DashboardRH = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get('/rh/dashboard');
        setData(res.data);
      } catch (err) {
        console.error('Erreur chargement dashboard RH:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-10 h-10 text-cyan-500 animate-spin" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-20 text-slate-500">
        <AlertCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
        <p>Impossible de charger les donnees RH</p>
      </div>
    );
  }

  const { totalAgents, moisCourant, evolution, alertes, repartitionSites, kpis } = data;

  const alertIcon = (type) => {
    if (type === 'danger') return <AlertTriangle className="w-5 h-5 text-red-400" />;
    if (type === 'warning') return <AlertCircle className="w-5 h-5 text-amber-400" />;
    return <Info className="w-5 h-5 text-blue-400" />;
  };

  const alertBg = (type) => {
    if (type === 'danger') return 'border-red-500/30 bg-red-950/30';
    if (type === 'warning') return 'border-amber-500/30 bg-amber-950/30';
    return 'border-blue-500/30 bg-blue-950/30';
  };

  return (
    <div className="space-y-6" data-testid="rh-dashboard">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 flex items-center gap-3">
          <div className="p-2.5 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl">
            <Users className="w-6 h-6 text-white" />
          </div>
          Tableau de Bord RH
        </h1>
        <p className="text-slate-500 mt-1 text-sm">Synthese mensuelle des couts salariaux et indicateurs</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="rh-kpis">
        <Card className="bg-white border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide font-medium">Agents actifs</p>
                <p className="text-2xl font-bold text-slate-800 mt-1">{totalAgents}</p>
                <p className="text-xs text-slate-400 mt-0.5">{moisCourant.nbAgents || 0} avec gardes</p>
              </div>
              <div className="p-3 bg-cyan-50 rounded-xl">
                <Users className="w-6 h-6 text-cyan-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide font-medium">Heures ce mois</p>
                <p className="text-2xl font-bold text-slate-800 mt-1">{moisCourant.heures || 0}h</p>
                <p className="text-xs text-slate-400 mt-0.5">{kpis.heuresMoyennesAgent}h / agent</p>
              </div>
              <div className="p-3 bg-violet-50 rounded-xl">
                <Clock className="w-6 h-6 text-violet-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide font-medium">Cout total</p>
                <p className="text-2xl font-bold text-slate-800 mt-1">{(moisCourant.coutTotal || 0).toLocaleString()} EUR</p>
                <div className="flex items-center gap-1 mt-0.5">
                  {kpis.variationMasse > 0 ? (
                    <TrendingUp className="w-3 h-3 text-red-500" />
                  ) : (
                    <TrendingDown className="w-3 h-3 text-emerald-500" />
                  )}
                  <p className={`text-xs font-medium ${kpis.variationMasse > 0 ? 'text-red-500' : 'text-emerald-500'}`}>
                    {kpis.variationMasse > 0 ? '+' : ''}{kpis.variationMasse}%
                  </p>
                </div>
              </div>
              <div className="p-3 bg-emerald-50 rounded-xl">
                <DollarSign className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide font-medium">Heures nuit</p>
                <p className="text-2xl font-bold text-slate-800 mt-1">{kpis.tauxHeuresNuit}%</p>
                <p className="text-xs text-slate-400 mt-0.5">{moisCourant.heuresNuit || 0}h de nuit</p>
              </div>
              <div className="p-3 bg-amber-50 rounded-xl">
                <Moon className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Evolution des couts */}
        <Card className="bg-white border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-slate-700 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-cyan-500" />
              Evolution des couts (6 mois)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={evolution} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="mois" tick={{ fontSize: 11, fill: '#64748b' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#64748b' }} />
                  <Tooltip
                    contentStyle={{ background: '#0f172a', border: 'none', borderRadius: 8, color: '#e2e8f0', fontSize: 12 }}
                    formatter={(value) => [`${value.toLocaleString()} EUR`, '']}
                  />
                  <Bar dataKey="masseBrute" name="Masse brute" fill="#06b6d4" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="chargesPatronales" name="Charges" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Evolution des heures */}
        <Card className="bg-white border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-slate-700 flex items-center gap-2">
              <Clock className="w-4 h-4 text-violet-500" />
              Evolution des heures (6 mois)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={evolution} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="mois" tick={{ fontSize: 11, fill: '#64748b' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#64748b' }} />
                  <Tooltip
                    contentStyle={{ background: '#0f172a', border: 'none', borderRadius: 8, color: '#e2e8f0', fontSize: 12 }}
                    formatter={(value) => [`${value}h`, '']}
                  />
                  <Line type="monotone" dataKey="heures" name="Total" stroke="#06b6d4" strokeWidth={2.5} dot={{ fill: '#06b6d4', r: 4 }} />
                  <Line type="monotone" dataKey="heuresNuit" name="Nuit" stroke="#f59e0b" strokeWidth={2} dot={{ fill: '#f59e0b', r: 3 }} />
                  <Line type="monotone" dataKey="heuresDimanche" name="Dimanche" stroke="#8b5cf6" strokeWidth={2} dot={{ fill: '#8b5cf6', r: 3 }} />
                  <Legend />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Repartition sites */}
        <Card className="bg-white border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-slate-700 flex items-center gap-2">
              <Building2 className="w-4 h-4 text-emerald-500" />
              Repartition par site
            </CardTitle>
          </CardHeader>
          <CardContent>
            {repartitionSites && repartitionSites.length > 0 ? (
              <div className="h-52">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={repartitionSites}
                      dataKey="gardes"
                      nameKey="site"
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={80}
                      paddingAngle={2}
                    >
                      {repartitionSites.map((_, idx) => (
                        <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ background: '#0f172a', border: 'none', borderRadius: 8, color: '#e2e8f0', fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="flex items-center justify-center h-52 text-slate-400 text-sm">
                <div className="text-center">
                  <Calendar className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  Aucune garde ce mois
                </div>
              </div>
            )}
            {repartitionSites && repartitionSites.length > 0 && (
              <div className="mt-3 space-y-1.5">
                {repartitionSites.slice(0, 4).map((s, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                      <span className="text-slate-600 truncate max-w-[160px]">{s.site}</span>
                    </div>
                    <span className="font-medium text-slate-800">{s.gardes}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Alertes */}
        <Card className="bg-white border-slate-200 lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-slate-700 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              Alertes RH ({alertes.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {alertes.length === 0 ? (
              <div className="flex items-center justify-center h-40 text-emerald-500 text-sm">
                <div className="text-center">
                  <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-2">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  </div>
                  Aucune alerte - Tout est en ordre
                </div>
              </div>
            ) : (
              <div className="space-y-3 max-h-72 overflow-y-auto" data-testid="rh-alertes">
                {alertes.map((alerte, i) => (
                  <div key={i} className={`border rounded-lg p-3 ${alertBg(alerte.type)}`}>
                    <div className="flex items-start gap-3">
                      {alertIcon(alerte.type)}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm text-slate-800">{alerte.titre}</p>
                        <p className="text-xs text-slate-500 mt-0.5">{alerte.message}</p>
                        {alerte.details && alerte.details.length > 0 && (
                          <div className="mt-1.5 flex flex-wrap gap-1">
                            {alerte.details.map((d, j) => (
                              <span key={j} className="text-[10px] px-2 py-0.5 bg-white/50 rounded-full text-slate-600">{d}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Monthly Summary Table */}
      <Card className="bg-white border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold text-slate-700 flex items-center gap-2">
            <Calendar className="w-4 h-4 text-blue-500" />
            Historique mensuel
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="rh-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left p-3 font-medium text-slate-600">Mois</th>
                  <th className="text-right p-3 font-medium text-slate-600">Agents</th>
                  <th className="text-right p-3 font-medium text-slate-600">Heures</th>
                  <th className="text-right p-3 font-medium text-slate-600">Nuit</th>
                  <th className="text-right p-3 font-medium text-slate-600">Gardes</th>
                  <th className="text-right p-3 font-medium text-slate-600">Masse brute</th>
                  <th className="text-right p-3 font-medium text-slate-600">Charges</th>
                  <th className="text-right p-3 font-medium text-slate-600 text-cyan-700">Cout total</th>
                </tr>
              </thead>
              <tbody>
                {evolution.map((m, i) => (
                  <tr key={i} className={`border-b border-slate-100 ${i === evolution.length - 1 ? 'bg-cyan-50/50 font-medium' : 'hover:bg-slate-50'}`}>
                    <td className="p-3 text-slate-700">{m.mois}</td>
                    <td className="p-3 text-right text-slate-600">{m.nbAgents}</td>
                    <td className="p-3 text-right text-slate-600">{m.heures}h</td>
                    <td className="p-3 text-right text-slate-600">{m.heuresNuit}h</td>
                    <td className="p-3 text-right text-slate-600">{m.nbShifts}</td>
                    <td className="p-3 text-right text-slate-600">{m.masseBrute.toLocaleString()} EUR</td>
                    <td className="p-3 text-right text-slate-600">{m.chargesPatronales.toLocaleString()} EUR</td>
                    <td className="p-3 text-right font-semibold text-cyan-700">{m.coutTotal.toLocaleString()} EUR</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardRH;
